import React, { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import { Mail, PlusCircle, SlidersHorizontal, X, Check } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { PageHeader, EmptyState } from '@/components/shared'
import { employeeService } from '@/services/employeeService'

const TeamDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')

  // Fetch active employees
  const { data: employeesData, isLoading } = useQuery(
    'activeEmployees',
    employeeService.getActiveEmployees,
    {
      onError: () => {
        toast.error('Failed to fetch team members')
      }
    }
  )

  const employees = employeesData?.data?.employees || []

  // Filter employees based on search term and department
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const matchesSearch = 
        employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.job_title?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesDepartment = !departmentFilter || employee.department === departmentFilter

      return matchesSearch && matchesDepartment
    })
  }, [employees, searchTerm, departmentFilter])

  // Get unique departments for filter
  const departments = useMemo(() => {
    const employeeDepartments = [...new Set(employees.map(emp => emp.department).filter(Boolean))]
    const commonDepartments = ['Engineering', 'HR', 'Marketing', 'Sales', 'Finance', 'Operations', 'Design', 'Support']
    return [...new Set([...employeeDepartments, ...commonDepartments])].sort()
  }, [employees])

  const getRoleBadge = (role) => {
    const config = {
      admin: { className: 'bg-red-500 hover:bg-red-500/80', label: 'Admin' },
      hr: { className: 'bg-blue-500 hover:bg-blue-500/80', label: 'HR' },
      manager: { className: 'bg-green-500 hover:bg-green-500/80', label: 'Manager' },
      employee: { className: '', label: 'Employee' },
    }
    const { className, label } = config[role] || { className: '', label: role }
    return (
      <Badge variant={role === 'employee' ? 'secondary' : 'default'} className={className}>
        {label}
      </Badge>
    )
  }

  const hasFilters = searchTerm || departmentFilter

  const clearFilters = () => {
    setSearchTerm('')
    setDepartmentFilter('')
  }

  return (
    <div>
      <PageHeader
        title="Team Directory"
        description="Browse and find your team members"
        toolbar={
          <>
            <div className="relative w-full md:w-auto md:min-w-[150px] lg:min-w-[180px]">
              <Input
                placeholder="Filter by name, email, department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 text-sm px-3 py-1"
              />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-3 border-dashed text-sm font-normal">
                  <PlusCircle className="mr-2 h-3.5 w-3.5" />
                  Department
                  {departmentFilter && (
                    <>
                      <Separator orientation="vertical" className="mx-2 h-3.5" />
                      <Badge variant="secondary" className="rounded-sm px-1 text-xs font-normal">
                        {departmentFilter}
                      </Badge>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-0" align="start">
                <div className="p-2">
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1.5">Department</div>
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm"
                    onClick={() => setDepartmentFilter('')}
                  >
                    <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${!departmentFilter ? 'bg-primary border-primary' : 'border-input'}`}>
                      {!departmentFilter && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    All Departments
                  </div>
                  {departments.map(dept => (
                    <div
                      key={dept}
                      className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded-sm"
                      onClick={() => setDepartmentFilter(dept === departmentFilter ? '' : dept)}
                    >
                      <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${departmentFilter === dept ? 'bg-primary border-primary' : 'border-input'}`}>
                        {departmentFilter === dept && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      {dept}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-3 text-sm font-normal">
                <X className="mr-2 h-3.5 w-3.5" />
                Reset
              </Button>
            )}

            <div className="flex items-center gap-2 md:ml-auto">
              <Button variant="outline" size="sm" className="h-8 px-3 text-sm font-normal">
                <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
                View
              </Button>
            </div>
          </>
        }
      />

      {/* Results Summary */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{filteredEmployees.length}</span> of <span className="font-medium text-foreground">{employees.length}</span> members
        </p>
      </div>

      {/* Employee Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredEmployees.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon="users"
              title="No team members found"
              description={hasFilters ? 'Try adjusting your search criteria' : 'No active team members available'}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredEmployees.map((employee) => (
            <Card key={employee.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  {/* Avatar */}
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                      {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  {/* Name */}
                  <h3 className="font-semibold text-lg truncate max-w-full">
                    {employee.first_name} {employee.last_name}
                  </h3>

                  {/* Role Badge */}
                  {getRoleBadge(employee.user?.role)}

                  {/* Contact Info */}
                  <div className="space-y-2 w-full text-sm">
                    {employee.user?.email && (
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate text-xs">{employee.user.email}</span>
                      </div>
                    )}

                    {employee.department && (
                      <div className="pt-2">
                        <p className="text-xs font-medium">Department</p>
                        <p className="text-xs text-muted-foreground truncate">{employee.department}</p>
                      </div>
                    )}

                    {employee.job_title && (
                      <div>
                        <p className="text-xs font-medium">Position</p>
                        <p className="text-xs text-muted-foreground truncate">{employee.job_title}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default TeamDirectory
